import { Button, Stack, Typography } from '@chronosphereio/chrono-ui';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { StringParam, useQueryParam } from 'use-query-params';
import { useVizFrameworkConfig } from '@/plugin-system/model';
import { mergeTimeSeries, useDataQueryResult } from '../DataQueriesProvider';

export const SERIES_LIMIT = 1000;

export interface SafeModeProps {
  /**
   * Optional component that renders when rendering is disabled. i.e. Learn More button
   */
  Action?: React.ReactNode;
  /**
   * Content to be displayed when it is safe to render
   */
  children?: React.ReactNode;
  /**
   * The maximum number of series allowed for rendering to be considered safe
   * @default 1000
   */
  limit?: number;
  /**
   * Custom message to be displayed when rendering is disabled.
   */
  message?: string;
}

interface SafeModeProviderProps {
  isSafe: boolean;
}

const SafeModeContext = createContext<SafeModeProviderProps | undefined>(undefined);

export function SafeMode({ children, limit = SERIES_LIMIT, Action, message }: SafeModeProps) {
  const { queryResults } = useDataQueryResult({ dataTypeFilter: 'TimeSeries' });

  const { isSafe, mergedTimeSeries } = useMemo(() => {
    const mergedTimeSeries = mergeTimeSeries(queryResults);
    return { isSafe: mergedTimeSeries.length <= limit, mergedTimeSeries };
  }, [queryResults, limit]);

  const [showContent, setShowContent] = useState(isSafe);

  useEffect(() => {
    setShowContent(isSafe);
  }, [isSafe]);

  const ctx = useMemo(() => ({ isSafe }), [isSafe]);

  return (
    <SafeModeContext.Provider value={ctx}>
      {showContent ? (
        children
      ) : (
        <SafeModeMessage
          message={message}
          limit={limit}
          numSeries={mergedTimeSeries.length}
          onClick={() => {
            setShowContent(true);
          }}
          Action={Action}
        />
      )}
    </SafeModeContext.Provider>
  );
}

interface SafeModeMessageProps {
  numSeries: number;
  message?: string;
  onClick: () => void;
  limit?: number;
  Action?: React.ReactNode;
}

function SafeModeMessage({ numSeries, onClick, limit = SERIES_LIMIT, Action, message }: SafeModeMessageProps) {
  return (
    <Stack width="100%" height="100%" spacing={2} direction="column" justifyContent="center" alignItems="center">
      <div>
        {message ? (
          <Typography>{message}</Typography>
        ) : (
          <>
            <Typography>Rendering has been disabled because this chart has {numSeries} series.</Typography>
            <Typography>A chart with more than {limit} series may cause the page to break.</Typography>
          </>
        )}
      </div>
      {Action}
      <Button variant="tertiary" color="secondary" onClick={onClick}>
        Load anyway
      </Button>
    </Stack>
  );
}

export function useSafeMode() {
  return useContext(SafeModeContext);
}

/**
 * Hook that returns a boolean indicating if "safe mode" should be enabled or
 * not based on the viz framework config. Safe mode may be enabled temporarily
 * by adding "&safeMode=true" to the url.
 */
export function useSafeModeEnabled() {
  const config = useVizFrameworkConfig();

  // Safe mode may be enabled using a query param temporarily or using the
  // configuration setting.
  const [queryParamSafeMode] = useQueryParam('safeMode', StringParam);

  return (queryParamSafeMode === 'true' || config?.enableSafeMode) ?? false;
}
